import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AuthPages.css';

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const token = params.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const redirectTimer = useRef(null);

  // Cancel the post-success redirect if the user navigates away first —
  // otherwise the timer fires against an unmounted page and yanks them to
  // /login from wherever they went.
  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!token) {
      setError('Invalid or missing reset token. Please use the link from your email.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const data = await resetPassword(token, newPassword, confirmPassword);
      setSuccess(data.message || 'Password reset successfully! You can now sign in.');
      // Reset tokens are single-use. Drop it from the URL immediately, or a
      // refresh re-renders the form with a token the server has already spent
      // and the resubmit fails — which reads as "the reset didn't work" when it
      // actually did. Then send the user where they need to go.
      setParams({}, { replace: true });
      redirectTimer.current = setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">AI Broker</div>
        <h1>Reset password</h1>
        <p className="auth-subtitle">Choose a new password for your account</p>

        {error && <div className="auth-error">{error}</div>}
        {success && (
          <div className="auth-success">
            {success}
            <div style={{ marginTop: 8, fontSize: 13 }}>
              Taking you to sign in…
            </div>
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="auth-form">
            <label>
              New password
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Min 8 chars, 1 upper, 1 lower, 1 digit"
              />
            </label>
            <label>
              Confirm new password
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Re-enter password"
              />
            </label>
            <button type="submit" className="auth-btn primary" disabled={busy}>
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}

        <p className="auth-footer">
          <Link to="/login">← Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
